import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getFareConfig, getPeakSurcharge, estimateAllTiers, estimateFare, computeCtp, getSystemConfig } from '../../services/fareService';
import { createBooking, getPreferredDrivers, getQualifiedDriversList, applyPromoCode } from '../../services/bookingService';
import { FareEstimate } from '../../components/booking/BookingComponents';
import toast from 'react-hot-toast';
import { getRoutesWithScoring } from '../../services/routeService';
import { createRecurringBooking, createMultiVehicleBooking } from '../../services/bookingService';
import { generateOccurrencePreview, loadHolidays, formatPattern, MAX_SERIES_WEEKS, DAY_NAMES } from '../../services/recurringService';
import { MAX_VEHICLES_PER_GROUP, TIER_CAPACITIES, validateMultiVehicleConfig } from '../../services/multiVehicleService';
import PreLaunchBanner from '../../components/common/PreLaunchBanner';


const STEP_LABELS = {
  1: { title: 'When', icon: '📅' },
  2: { title: 'Who', icon: '👤' },
  3: { title: 'Where', icon: '📍' },
  4: { title: 'Stops', icon: '🛑' },
  6: { title: 'Accessibility', icon: '♿' },
  7: { title: 'Choose a car', icon: '🚗' },
  8: { title: 'Driver', icon: '🧑‍✈️' },
  9: { title: 'Review & Pay', icon: '💳' },
};
// Single-vehicle flow: When, Who, Where, Choose a car, Stops, Driver, Review.
// Multi-vehicle sets car + accessibility per vehicle in step 2, so it shows the
// trip-level Accessibility step instead of the single-car picker.
const stepOrderFor = (isMulti) => (isMulti ? [1, 2, 3, 6, 4, 8, 9] : [1, 2, 3, 7, 4, 8, 9]);

export default function BookingWizard() {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [fareConfig, setFareConfig] = useState(null);
  const [sysConfig, setSysConfig] = useState({ minAdvanceBookingMinutes: 60, maxAdvanceBookingDays: 30 });
  const [fareEstimates, setFareEstimates] = useState([]);
  // Recurrence (Batch 12)
  const [holidays, setHolidays] = useState([]);
  const [occurrencePreview, setOccurrencePreview] = useState({ dates: [], error: null });

const [preferredDrivers, setPreferredDrivers] = useState([]);
  const [showAllQualified, setShowAllQualified] = useState(false);
  const [allQualifiedDrivers, setAllQualifiedDrivers] = useState([]);
  const [loadingQualified, setLoadingQualified] = useState(false);

const [routeOptions, setRouteOptions] = useState({ routes: [], recommended: null, alternative: null });
  const [selectedRouteIndex, setSelectedRouteIndex] = useState(null);
  const [routesLoading, setRoutesLoading] = useState(false);
  const [booking, setBooking] = useState({
    // Step 1: When
    date: '', time: '', isPeak: false, peakPercent: 0,
    // Step 1 recurrence (Batch 12)
    isRecurring: false,
    recurrencePattern: null,  // { mode, daysOfWeek, startDate, weeks, dates, time, skipHolidays }
    // Step 2 multi-vehicle (Batch 13)
    isMultiVehicle: false,
    vehicles: [],             // [{ tier, passengerCount, passengerGroupName, luggage, childSeatRequired, accessibilityNeeded, hasMinor }]


    // Step 2: Who
    bookedFor: 'self', thirdPartyName: '', thirdPartyPhone: '', hasMinor: false,
    // Step 3: Where
    pickup: null, destination: null, pickupAddress: '', destinationAddress: '',
    estimatedDistance: 0, estimatedDuration: 0, routePolyline: '',
    // Step 4: Stops (Batch 11)
    stops: [],                      // [{ type: 'pickup'|'dropoff', address, location, coRiderName, passengerCountChange, isBookingRiderDropoff, expectedDwellSeconds }]
    bookingRiderTravelling: true,   // booking rider is in the vehicle at original pickup
    // Step 8: Driver
    driverPreference: 'auto', preferredDriverId: null,
    preferredDriverPool: [],   // multi-vehicle: pool of preferred driver IDs, distributed across vehicles

    // Step 5: Passengers
    passengerCount: 1, luggageSmall: 0, luggageLarge: 0, childSeatRequired: false,
    // Step 6: Accessibility
accessibilityNeeded: false,
assistanceRequired: false,
specialInstructions: '',
hasSupportAnimal: false,
supportAnimalDetails: '',
mobilityAidRequired: false,
cognitiveSupport: false,
emergencyContactName: '',
emergencyContactPhone: '',

    // Step 7: Tier
    tier: 'go',
    // Step 8: Payment
    promoCode: '', promoDiscount: 0, useWalletCredit: false, walletCreditAmount: 0,
  });

  useEffect(() => {
    getFareConfig().then(setFareConfig);
    getSystemConfig().then(setSysConfig).catch(() => {});
    if (userProfile?.id) {
      getPreferredDrivers(userProfile.id).then(setPreferredDrivers);
    }
    // Load NSW public holidays (cached) for recurrence preview
    loadHolidays().then(setHolidays).catch(() => setHolidays([]));
  }, [userProfile]);

  // Refresh occurrence preview when recurrence pattern changes
  useEffect(() => {
    if (booking.isRecurring && booking.recurrencePattern) {
      const result = generateOccurrencePreview(booking.recurrencePattern, holidays);
      setOccurrencePreview(result);
    } else {
      setOccurrencePreview({ dates: [], error: null });
    }
  }, [booking.isRecurring, booking.recurrencePattern, holidays]);


  const update = (field, value) => setBooking((prev) => ({ ...prev, [field]: value }));

  // Calculate fares when route is known
  useEffect(() => {
    if (fareConfig && booking.estimatedDistance > 0) {
      const pickupTime = new Date(`${booking.date}T${booking.time}`);
      const peakMult = getPeakSurcharge(pickupTime, fareConfig.peak);
      update('isPeak', peakMult > 0);
      update('peakPercent', Math.round(peakMult * 100));

      const selectedRoute = routeOptions.routes[selectedRouteIndex];
      const tolls = selectedRoute?.totalToll || 0;
      const ctp = computeCtp(booking.estimatedDistance, booking.pickup?.lat, booking.pickup?.lng, fareConfig?.rates?.ctp);

      const estimates = estimateAllTiers({
        distanceKm: booking.estimatedDistance, durationMin: booking.estimatedDuration,
        peakMultiplier: peakMult, tolls, ctp, rates: fareConfig.rates,
        stops: booking.stops,
      });


      setFareEstimates(estimates);
    }
  }, [fareConfig, booking.estimatedDistance, booking.estimatedDuration, booking.date, booking.time, routeOptions, selectedRouteIndex, booking.pickup]);


  const getSelectedFare = () => fareEstimates.find((f) => f.tier === booking.tier);

  // Per-vehicle fares for a multi-vehicle booking. Each vehicle shares the trip
  // (distance/duration/route tolls/peak/stops) but has its own tier — and its own
  // $1.20 levy, which estimateFare already includes per call. Returns
  // [{ ...vehicle, fareBreakdown }] plus the grand total.
  const getMultiVehicleFares = () => {
    if (!fareConfig || !(booking.estimatedDistance > 0)) return { vehicles: [], grandTotal: 0 };
    const pickupTime = new Date(`${booking.date}T${booking.time}`);
    const peakMult = getPeakSurcharge(pickupTime, fareConfig.peak);
    const tolls = routeOptions.routes[selectedRouteIndex]?.totalToll || 0;
    const ctp = computeCtp(booking.estimatedDistance, booking.pickup?.lat, booking.pickup?.lng, fareConfig?.rates?.ctp);
    const priced = (booking.vehicles || []).map((v) => ({
      ...v,
      fareBreakdown: estimateFare({
        distanceKm: booking.estimatedDistance,
        durationMin: booking.estimatedDuration,
        tier: v.tier,
        peakMultiplier: peakMult,
        tolls,
        ctp,
        rates: fareConfig.rates,
        stops: booking.stops,
      }),
    }));
    const grandTotal = Math.round(priced.reduce((s, v) => s + (v.fareBreakdown?.totalFare || 0), 0) * 100) / 100;
    return { vehicles: priced, grandTotal };
  };

  const validateStep = () => {
    switch (step) {
      case 1: {
        const minMin = sysConfig.minAdvanceBookingMinutes || 0;
        const minMs = minMin * 60000;
        const maxMs = (sysConfig.maxAdvanceBookingDays || 30) * 86400000;
        const leadLabel = minMin <= 0 ? 'now' : (minMin % 60 === 0 ? `${minMin / 60} hour${minMin / 60 > 1 ? 's' : ''}` : `${minMin} minutes`);
        if (booking.isRecurring) {
          // Recurring path: validate the pattern produces at least one occurrence
          if (!booking.recurrencePattern) { toast.error('Set up your recurrence schedule', { id: 'recurrence-required' }); return false; }
          if (occurrencePreview.error) { toast.error(occurrencePreview.error, { id: 'recurrence-invalid' }); return false; }
          if (occurrencePreview.dates.length === 0) { toast.error('Recurrence produces no valid dates', { id: 'recurrence-empty' }); return false; }
          // Each occurrence must be at least 1hr in the future; we check the first one
          const firstAt = new Date(occurrencePreview.dates[0].scheduledAt);
          if (firstAt < new Date(Date.now() + minMs)) {
            toast.error(minMin <= 0 ? 'First occurrence cannot be in the past' : `First occurrence must be at least ${leadLabel} from now`, { id: 'first-too-soon' });
            return false;
          }
          return true;
        }
        // One-off path (existing logic)
        if (!booking.date || !booking.time) { toast.error('Select date and time', { id: 'date-required' }); return false; }
        const pickupTime = new Date(`${booking.date}T${booking.time}`);
        if (pickupTime < new Date(Date.now() + minMs)) { toast.error(minMin <= 0 ? 'Pickup time cannot be in the past' : `Minimum ${leadLabel} advance booking`, { id: 'min-date' }); return false; }
        if (pickupTime > new Date(Date.now() + maxMs)) { toast.error(`Maximum ${sysConfig.maxAdvanceBookingDays || 30} days advance`, { id: 'max-date' }); return false; }
        return true;
      }
      case 2: {
        if (booking.bookedFor === 'thirdParty' && (!booking.thirdPartyName || !booking.thirdPartyPhone)) {
          toast.error('Enter third-party details', { id: 'third-party' }); return false;
        }
        // Minor passenger rule: if hasMinor, an adult on booking must travel
        if (booking.hasMinor && !booking.bookingRiderTravelling) {
          toast.error('Minor passengers must be accompanied by an adult on the booking', { id: 'minor-adult' });
          return false;
        }
        // Multi-vehicle validation (Batch 13)
        if (booking.isMultiVehicle) {
          const totalPax = (booking.vehicles || []).reduce((s, v) => s + (v.passengerCount || 0), 0);
          const validation = validateMultiVehicleConfig(booking.vehicles, totalPax);
          if (!validation.valid) {
            toast.error(validation.error, { id: 'multi-vehicle-invalid' });
            return false;
          }
          // Multi-vehicle is not compatible with recurring in v1
          if (booking.isRecurring) {
            toast.error('Multi-vehicle bookings cannot be recurring in v1. Cancel one or the other.', { id: 'multi-vehicle-recurring' });
            return false;
          }
          // Multi-vehicle with minors: each vehicle with a minor must have an adult travelling
          if (booking.vehicles.some(v => v.hasMinor) && !booking.bookingRiderTravelling) {
            toast.error('Vehicles with minor passengers require an adult on the booking to travel', { id: 'multi-vehicle-minor' });
            return false;
          }
        }
        return true;
      }
      case 3: {
        if (!booking.pickup || !booking.destination) { toast.error('Enter pickup and destination', { id: 'address-required' }); return false; }
        if (selectedRouteIndex == null || !routeOptions.routes[selectedRouteIndex]) { toast.error('Select a route', { id: 'route-required' }); return false; }
        return true;
      }
      case 4: {
        // Stops validation (Batch 11)
        const stops = booking.stops || [];
        if (stops.length > 4) {
          toast.error('Maximum 4 intermediate stops', { id: 'too-many-stops' });
          return false;
        }
        for (let i = 0; i < stops.length; i++) {
          const s = stops[i];
          if (!s.address || !s.location) {
            toast.error(`Stop ${i + 1}: please select an address`, { id: 'stop-address' });
            return false;
          }
          if (s.type === 'pickup' && !s.coRiderName?.trim()) {
            toast.error(`Stop ${i + 1}: please enter the passenger's first name`, { id: 'co-rider-name' });
            return false;
          }
        }
        // Capacity check across the trip (using current passengerCount)
        // The booking rider always travels through to the final destination, so no
        // intermediate "get out" handling is needed here.
        return true;
      }
      case 5: {
        // Passengers
        const maxPax = booking.tier === 'plus' || booking.tier === 'vipPlus' ? 6 : 4;
        if (booking.passengerCount < 1 || booking.passengerCount > maxPax) {
          toast.error(`1-${maxPax} passengers for this tier`, { id: 'pax-limit' });
          return false;
        }
        return true;
      }
      case 8: {
        // Multi-vehicle: if preferred, require at least one driver in the pool
        if (booking.isMultiVehicle && booking.driverPreference === 'preferred') {
          if (!(booking.preferredDriverPool || []).length) {
            toast.error('Select at least one preferred driver, or choose auto-assign', { id: 'pool-empty' });
            return false;
          }
          return true;
        }
        // Single-vehicle — validate compatibility if a preferred driver is selected
        if (booking.driverPreference === 'preferred' && booking.preferredDriverId) {
          const allDrivers = [...preferredDrivers, ...allQualifiedDrivers];
          const selected = allDrivers.find((d) => d.id === booking.preferredDriverId);
          if (selected && selected.vehicleMaxPassengers && booking.passengerCount > selected.vehicleMaxPassengers) {
            toast.error(`This driver's vehicle holds ${selected.vehicleMaxPassengers} passengers but you've selected ${booking.passengerCount}`, { id: 'driver-pax-incompat' });
            return false;
          }
        }
        return true;
      }
      default: return true;
    }
  };



  const nextStep = () => {
    if (!validateStep()) return;
    const order = stepOrderFor(booking.isMultiVehicle);
    setStep((s) => {
      const i = order.indexOf(s);
      if (i === -1) return order[order.length - 1];
      return order[Math.min(i + 1, order.length - 1)];
    });
  };
  const prevStep = () => {
    const order = stepOrderFor(booking.isMultiVehicle);
    setStep((s) => {
      const i = order.indexOf(s);
      if (i <= 0) return order[0];
      return order[i - 1];
    });
  };
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const fare = getSelectedFare();

      // ─── Multi-vehicle path (Batch 13) ───
      if (booking.isMultiVehicle) {
        // Distribute the preferred-driver pool across vehicles (one each, in order).
        // Vehicles without a pool driver — and any whose driver is unavailable — fall back to auto-match server-side.
        const pool = booking.driverPreference === 'preferred' ? (booking.preferredDriverPool || []) : [];
        const pricedVehicles = getMultiVehicleFares().vehicles.map((v, i) => ({
          ...v,
          preferredDriverId: pool[i] || null,
        }));
        const mvResult = await createMultiVehicleBooking({
          riderName: `${userProfile.firstName || ''} ${userProfile.lastName || ''}`.trim() || null,
          vehicles: pricedVehicles,
          scheduledPickupTime: new Date(`${booking.date}T${booking.time}`).toISOString(),
          pickup: booking.pickup,
          destination: booking.destination,
          pickupAddress: booking.pickupAddress,
          destinationAddress: booking.destinationAddress,
          estimatedDistance: booking.estimatedDistance,
          estimatedDuration: booking.estimatedDuration,
          routePolyline: booking.routePolyline,
          routeSummary: routeOptions.routes[selectedRouteIndex]?.summary || '',
          bookingRiderTravelling: booking.bookingRiderTravelling,
          specialInstructions: booking.specialInstructions,
        });
        toast.success(`Group ${mvResult.vehicleGroupReferenceNumber} created — matching ${mvResult.vehicleCount} drivers in parallel...`);
        navigate(`/rider/vehicle-group/${mvResult.vehicleGroupId}`);
        return;
      }

      // ─── Recurring path (Batch 12) ───
      if (booking.isRecurring && booking.recurrencePattern) {
        const recurringResult = await createRecurringBooking({
          riderName: `${userProfile.firstName || ''} ${userProfile.lastName || ''}`.trim() || null,
          pattern: booking.recurrencePattern,
          pickup: booking.pickup,
          destination: booking.destination,
          pickupAddress: booking.pickupAddress,
          destinationAddress: booking.destinationAddress,
          estimatedDistance: booking.estimatedDistance,
          estimatedDuration: booking.estimatedDuration,
          routePolyline: booking.routePolyline,
          routeSummary: routeOptions.routes[selectedRouteIndex]?.summary || '',
          bookedFor: booking.bookedFor,
          thirdPartyName: booking.thirdPartyName,
          thirdPartyPhone: booking.thirdPartyPhone,
          hasMinor: booking.hasMinor,
          bookingRiderTravelling: booking.bookingRiderTravelling,
          stops: booking.stops,
          passengerCount: booking.passengerCount,
          luggage: { small: booking.luggageSmall, large: booking.luggageLarge },
          childSeatRequired: booking.childSeatRequired,
          accessibilityNeeded: booking.accessibilityNeeded,
          assistanceRequired: booking.assistanceRequired,
          specialInstructions: booking.specialInstructions,
          // Batch 10 vulnerable-passenger fields (applied to every occurrence)
          hasSupportAnimal: booking.hasSupportAnimal,
          supportAnimalDetails: booking.supportAnimalDetails,
          mobilityAidRequired: booking.mobilityAidRequired,
          cognitiveSupport: booking.cognitiveSupport,
          emergencyContactName: booking.emergencyContactName,
          emergencyContactPhone: booking.emergencyContactPhone,
          tier: booking.tier,
        });

        toast.success(`Series ${recurringResult.seriesReferenceNumber} created — ${recurringResult.occurrenceCount} occurrences. Fares are being calculated.`);
        navigate(`/rider/series/${recurringResult.seriesId}`);
        return;
      }

      // ─── One-off path (existing) ───
      const result = await createBooking({
        riderId: userProfile.id,
        riderName: `${userProfile.firstName || ''} ${userProfile.lastName || ''}`.trim() || null,
        scheduledPickupTime: new Date(`${booking.date}T${booking.time}`).toISOString(),

        pickup: booking.pickup,
        destination: booking.destination,
        pickupAddress: booking.pickupAddress,
        destinationAddress: booking.destinationAddress,
        estimatedDistance: booking.estimatedDistance,
        estimatedDuration: booking.estimatedDuration,
        routePolyline: booking.routePolyline,
        routeSummary: routeOptions.routes[selectedRouteIndex]?.summary || '',
        bookedFor: booking.bookedFor,
        thirdPartyName: booking.thirdPartyName,
        thirdPartyPhone: booking.thirdPartyPhone,
        hasMinor: booking.hasMinor,
        bookingRiderTravelling: booking.bookingRiderTravelling,
        stops: booking.stops,
        passengerCount: booking.passengerCount,
        luggage: { small: booking.luggageSmall, large: booking.luggageLarge },
        childSeatRequired: booking.childSeatRequired,
        accessibilityNeeded: booking.accessibilityNeeded,
        assistanceRequired: booking.assistanceRequired,
        specialInstructions: booking.specialInstructions,
        // Batch 10 vulnerable-passenger fields
        hasSupportAnimal: booking.hasSupportAnimal,
        supportAnimalDetails: booking.supportAnimalDetails,
        mobilityAidRequired: booking.mobilityAidRequired,
        cognitiveSupport: booking.cognitiveSupport,
        emergencyContactName: booking.emergencyContactName,
        emergencyContactPhone: booking.emergencyContactPhone,

        tier: booking.tier,
        fareBreakdown: fare,
        driverPreference: booking.driverPreference,
        preferredDriverId: booking.preferredDriverId,
        promoCode: booking.promoCode,
        promoDiscount: booking.promoDiscount,
        useWalletCredit: booking.useWalletCredit,
      });

      toast.success(`Booking confirmed! Ref: ${result.referenceNumber}`);
      navigate(`/rider/trip/${result.bookingId}`);
    } catch (err) {
      toast.error(err.message || 'Booking failed');
    }
    setSubmitting(false);
  };

  const handleApplyPromo = async () => {
    if (!booking.promoCode) return;
    try {
      const fare = getSelectedFare();
      const result = await applyPromoCode(booking.promoCode, userProfile.id, booking.tier, fare?.totalFare || 0);
      if (result.valid) {
        update('promoDiscount', result.discount);
        toast.success(`Promo applied! -$${result.discount.toFixed(2)}`);
      } else {
        toast.error(result.reason || 'Invalid promo code');
      }
    } catch (err) { toast.error('Invalid promo code'); }
  };
//=============================================================
  // Google Places Autocomplete handler
  const initAutocomplete = useCallback((inputId, field, addressField) => {
    if (!window.google) return;
    const input = document.getElementById(inputId);
    if (!input) return;
    const autocomplete = new window.google.maps.places.Autocomplete(input, {
      componentRestrictions: { country: 'au' }, fields: ['geometry', 'formatted_address', 'place_id'],
    });
    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (place.geometry) {
        setBooking(prev => ({
          ...prev,
          [field]: { lat: place.geometry.location.lat(), lng: place.geometry.location.lng(), placeId: place.place_id },
          [addressField]: place.formatted_address,
        }));
      }
    });
    // Also clear state when the user empties the input or edits it without selecting
    input.addEventListener('input', (e) => {
      if (!e.target.value.trim()) {
        setBooking(prev => ({ ...prev, [field]: null, [addressField]: '' }));
      }
    });
  }, []);


  const fetchRouteOptions = useCallback(async () => {
    const pickup = booking.pickup;
    const dest = booking.destination;
    if (!pickup || !dest || !fareConfig) return;

    const pickupTime = booking.date && booking.time
      ? new Date(`${booking.date}T${booking.time}`)
      : new Date(Date.now() + ((sysConfig.minAdvanceBookingMinutes || 0) * 60000 || 3600000));
    const peakMult = getPeakSurcharge(pickupTime, fareConfig.peak);

    setRoutesLoading(true);
    try {
      const result = await getRoutesWithScoring({
        pickup, destination: dest, pickupTime,
        tier: 'go', // score on Go for relative ranking; per-tier fares re-displayed in Step 7
        fareRates: fareConfig.rates,
        peakMultiplier: peakMult,
        stops: booking.stops,
      });

      setRouteOptions(result);
      if (result.recommended) {
        const idx = result.routes.findIndex(r => r.index === result.recommended.index);
        setSelectedRouteIndex(idx >= 0 ? idx : 0);
      } else {
        setSelectedRouteIndex(null);
      }
    } catch (err) {
      console.error('Route fetch failed:', err);
      setRouteOptions({ routes: [], recommended: null, alternative: null });
      setSelectedRouteIndex(null);
    }
    setRoutesLoading(false);
  }, [booking.pickup, booking.destination, booking.date, booking.time, fareConfig, sysConfig]);



  useEffect(() => {
    if (step === 3) {
      setTimeout(() => {
        initAutocomplete('pickup-input', 'pickup', 'pickupAddress');
        initAutocomplete('destination-input', 'destination', 'destinationAddress');
      }, 200);
    }
  }, [step, initAutocomplete]);

  // Refetch routes when addresses, pickup time, or stops change; clear when either address is missing
  useEffect(() => {
    if (booking.pickup && booking.destination && fareConfig) {
      fetchRouteOptions();
    } else {
      setRouteOptions({ routes: [], recommended: null, alternative: null });
      setSelectedRouteIndex(null);
      setBooking(prev => ({ ...prev, estimatedDistance: 0, estimatedDuration: 0, routePolyline: '' }));
    }
  }, [booking.pickup, booking.destination, booking.date, booking.time, booking.stops, fareConfig, fetchRouteOptions]);



  // Sync selected route's metadata into booking state
  useEffect(() => {
    if (selectedRouteIndex == null) return;
    const route = routeOptions.routes[selectedRouteIndex];
    if (!route) return;
    setBooking(prev => ({
      ...prev,
      estimatedDistance: route.distanceKm,
      estimatedDuration: route.durationMin,
      routePolyline: (route.path || []).map(p => `${p.lat()},${p.lng()}`).join('|'),
    }));
  }, [selectedRouteIndex, routeOptions]);




  // Accessibility & support fields, shared by the multi-vehicle Accessibility step
  // and the single-vehicle car-selection step (folded in, per product decision).
  // Trip-level extras (apply to the whole booking, not a specific vehicle).
  const renderSpecialInstructions = () => (
    <div className="form-group">
      <label className="form-label">Special instructions (optional)</label>
      <textarea className="form-input" rows={3} maxLength={200} value={booking.specialInstructions}
        onChange={(e) => update('specialInstructions', e.target.value)}
        placeholder="e.g. Gate code, specific entrance, mobility notes..." />
      <div className="text-[0.8rem] text-muted text-right">{booking.specialInstructions.length}/200</div>
    </div>
  );
  const renderAdditionalSupport = () => (
    <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200 mb-3">
      <div className="font-semibold text-brand-dark mb-2 text-[0.9rem]">Additional support (optional)</div>
      <label className="flex items-center gap-2.5 mb-2 cursor-pointer">
        <input type="checkbox" checked={!!booking.hasSupportAnimal} onChange={(e) => update('hasSupportAnimal', e.target.checked)} />
        <span className="text-[0.9rem]">🐕 Guide or support animal travelling</span>
      </label>
      {booking.hasSupportAnimal && (
        <input className="form-input mb-2" placeholder="Type of animal and any details (optional)"
          value={booking.supportAnimalDetails || ''} onChange={(e) => update('supportAnimalDetails', e.target.value)} />
      )}
      <label className="flex items-center gap-2.5 mb-2 cursor-pointer">
        <input type="checkbox" checked={!!booking.mobilityAidRequired} onChange={(e) => update('mobilityAidRequired', e.target.checked)} />
        <span className="text-[0.9rem]">♿ Mobility aid (wheelchair, walker, etc.)</span>
      </label>
      <label className="flex items-center gap-2.5 cursor-pointer">
        <input type="checkbox" checked={!!booking.cognitiveSupport} onChange={(e) => update('cognitiveSupport', e.target.checked)} />
        <span className="text-[0.9rem]">🧠 Cognitive or communication support needed</span>
      </label>
    </div>
  );
  const renderEmergencyContact = () => (
    <div className="form-group">
      <label className="form-label">Emergency contact (optional, for this trip only)</label>
      <input className="form-input mb-1.5" placeholder="Contact name"
        value={booking.emergencyContactName || ''} onChange={(e) => update('emergencyContactName', e.target.value)} />
      <input className="form-input" type="tel" placeholder="Phone number"
        value={booking.emergencyContactPhone || ''} onChange={(e) => update('emergencyContactPhone', e.target.value)} />
      <div className="text-[0.75rem] text-muted mt-1">Only used if there's an incident during the trip.</div>
    </div>
  );

  return (
    <div className="max-w-[600px] mx-auto p-6">
      <div className="mb-4">
        <button onClick={() => navigate('/rider')} className="bg-transparent border-0 text-brand-primary font-semibold text-[0.9rem] cursor-pointer p-0">← Dashboard</button>
      </div>
      <PreLaunchBanner variant="inline" />
      {/* Progress Bar */}
      {(() => {
        const order = stepOrderFor(booking.isMultiVehicle);
        const pos = order.indexOf(step);
        const label = STEP_LABELS[step] || { title: '', icon: '' };
        return (
          <>
            <div className="flex items-center gap-1 mb-8">
              {order.map((id, idx) => (
                <div key={id} className="flex-1 h-1 rounded-sm" style={{ background: idx <= pos ? '#2dd4a8' : '#e2e8f0', transition: 'background 0.3s' }} />
              ))}
            </div>
            <div className="text-center mb-6">
              <span className="text-[1.5rem]">{label.icon}</span>
              <h2 className="font-serif mt-2 mb-1 text-brand-dark">
                Step {pos + 1}: {label.title}
              </h2>
            </div>
          </>
        );
      })()}

      {/* STEP 1: When */}
      {step === 1 && (
        <div>
          {/* Recurring toggle */}
          <div className="p-3.5 rounded-xl border-[1.5px] border-slate-200 mb-4"
            style={{ background: booking.isRecurring ? 'rgba(99,102,241,0.04)' : '#fff' }}>
            <label className="flex items-center gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={!!booking.isRecurring}
                onChange={(e) => {
                  update('isRecurring', e.target.checked);
                  if (e.target.checked && !booking.recurrencePattern) {
                    // Initialise sensible defaults
                    const tomorrow = new Date(Date.now() + 86400000);
                    update('recurrencePattern', {
                      mode: 'weekly',
                      daysOfWeek: [1, 2, 3, 4, 5], // Mon-Fri
                      startDate: tomorrow.toISOString().split('T')[0],
                      weeks: 4,
                      time: booking.time || '08:00',
                      skipHolidays: true,
                      dates: [],
                    });
                  }
                }}
              />
              <span className="font-semibold text-brand-dark">Make this a recurring booking</span>
            </label>
            <div className="text-[0.78rem] text-muted mt-1 ml-[26px]">
              Schedule multiple trips at once — up to {MAX_SERIES_WEEKS} weeks ahead. Each trip is priced individually but locked at booking.
            </div>
          </div>

          {/* One-off date/time inputs (only when NOT recurring) */}
          {!booking.isRecurring && (
            <>
              <div className="form-group">
                <label className="form-label">Date *</label>
                <input type="date" className="form-input" value={booking.date} onChange={(e) => update('date', e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  max={new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0]} />
              </div>
              <div className="form-group">
                <label className="form-label">Time *</label>
                <input type="time" className="form-input" value={booking.time} onChange={(e) => update('time', e.target.value)} step="900" />
              </div>
              {booking.isPeak && (
                <div className="p-3 rounded-xl mt-2" style={{ background: 'rgba(255,107,107,0.06)', border: '1px solid rgba(255,107,107,0.15)' }}>
                  <span className="font-semibold" style={{ color: '#ff6b6b' }}>Peak pricing: +{booking.peakPercent}% surcharge</span>
                </div>
              )}
            </>
          )}

          {/* Recurrence pattern editor */}
          {booking.isRecurring && booking.recurrencePattern && (() => {
            const p = booking.recurrencePattern;
            const updatePattern = (changes) => update('recurrencePattern', { ...p, ...changes });

            return (
              <div>
                {/* Mode toggle */}
                <div className="flex gap-2 mb-4">
                  <button onClick={() => updatePattern({ mode: 'weekly' })} className="flex-1 p-3 rounded-[10px] cursor-pointer font-semibold text-[0.9rem]"
                    style={{
                      border: `2px solid ${p.mode === 'weekly' ? '#2dd4a8' : '#e2e8f0'}`,
                      background: p.mode === 'weekly' ? 'rgba(45,212,168,0.06)' : '#fff',
                    }}>📅 Weekly</button>
                  <button onClick={() => updatePattern({ mode: 'custom' })} className="flex-1 p-3 rounded-[10px] cursor-pointer font-semibold text-[0.9rem]"
                    style={{
                      border: `2px solid ${p.mode === 'custom' ? '#2dd4a8' : '#e2e8f0'}`,
                      background: p.mode === 'custom' ? 'rgba(45,212,168,0.06)' : '#fff',
                    }}>📌 Custom dates</button>
                </div>

                {/* Weekly mode controls */}
                {p.mode === 'weekly' && (
                  <>
                    <div className="form-group">
                      <label className="form-label">Days of the week *</label>
                      <div className="flex gap-1.5 flex-wrap">
                        {DAY_NAMES.map((dn, idx) => {
                          const selected = (p.daysOfWeek || []).includes(idx);
                          return (
                            <button
                              key={idx}
                              onClick={() => {
                                const set = new Set(p.daysOfWeek || []);
                                if (selected) set.delete(idx); else set.add(idx);
                                updatePattern({ daysOfWeek: Array.from(set).sort() });
                              }}
                              className="px-3.5 py-2 rounded-full cursor-pointer font-semibold text-[0.85rem]"
                              style={{
                                border: `1.5px solid ${selected ? '#2dd4a8' : '#e2e8f0'}`,
                                background: selected ? '#2dd4a8' : '#fff',
                                color: selected ? '#fff' : '#0a1628',
                              }}
                            >{dn}</button>
                          );
                        })}
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Start date *</label>
                      <input
                        type="date"
                        className="form-input"
                        value={p.startDate || ''}
                        onChange={(e) => updatePattern({ startDate: e.target.value })}
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Number of weeks * ({p.weeks || 1})</label>
                      <input
                        type="range"
                        min="1"
                        max={MAX_SERIES_WEEKS}
                        value={p.weeks || 1}
                        onChange={(e) => updatePattern({ weeks: parseInt(e.target.value) })}
                        className="w-full"
                      />
                      <div className="flex justify-between text-[0.75rem] text-muted">
                        <span>1 week</span><span>{MAX_SERIES_WEEKS} weeks</span>
                      </div>
                    </div>
                  </>
                )}

                {/* Custom mode controls */}
                {p.mode === 'custom' && (
                  <>
                    <div className="form-group">
                      <label className="form-label">Pick dates *</label>
                      <div className="flex gap-1.5 mb-2">
                        <input
                          type="date"
                          className="form-input flex-1"
                          id="custom-date-add"
                          min={new Date().toISOString().split('T')[0]}
                          max={new Date(Date.now() + MAX_SERIES_WEEKS * 7 * 86400000).toISOString().split('T')[0]}
                        />
                        <button
                          onClick={() => {
                            const el = document.getElementById('custom-date-add');
                            if (!el?.value) return;
                            const existing = p.dates || [];
                            if (existing.includes(el.value)) return;
                            updatePattern({ dates: [...existing, el.value].sort() });
                            el.value = '';
                          }}
                          className="px-4 py-2 rounded-[10px] border-0 bg-brand-primary text-white cursor-pointer font-semibold"
                        >Add</button>
                      </div>
                      {(p.dates || []).length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {(p.dates || []).map((d) => (
                            <span key={d} className="px-2.5 py-1 rounded-full text-[0.8rem] font-semibold inline-flex items-center gap-1.5"
                              style={{ background: 'rgba(99,102,241,0.08)', color: '#4f46e5' }}>
                              {new Date(d).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })}
                              <button
                                onClick={() => updatePattern({ dates: (p.dates || []).filter((x) => x !== d) })}
                                className="bg-transparent border-0 cursor-pointer font-bold p-0 text-[0.95rem] leading-none"
                                style={{ color: '#ff6b6b' }}
                              >×</button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Time (shared between modes) */}
                <div className="form-group">
                  <label className="form-label">Pickup time *</label>
                  <input
                    type="time"
                    className="form-input"
                    value={p.time || ''}
                    onChange={(e) => updatePattern({ time: e.target.value })}
                    step="900"
                  />
                </div>

                {/* Skip holidays */}
                <div className="form-group">
                  <label className="flex items-center gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!p.skipHolidays}
                      onChange={(e) => updatePattern({ skipHolidays: e.target.checked })}
                    />
                    <span className="text-[0.9rem]">Skip NSW public holidays</span>
                  </label>
                </div>

                {/* Preview */}
                {occurrencePreview.error && (
                  <div className="p-3 rounded-[10px] mt-3" style={{ background: '#fef2f2', border: '1px solid #fecaca' }}>
                    <span className="text-[0.85rem] font-semibold" style={{ color: '#dc2626' }}>⚠ {occurrencePreview.error}</span>
                  </div>
                )}
                {!occurrencePreview.error && occurrencePreview.dates.length > 0 && (
                  <div className="p-3.5 rounded-xl mt-3" style={{ background: 'rgba(45,212,168,0.05)', border: '1px solid rgba(45,212,168,0.2)' }}>
                    <div className="font-bold text-brand-dark mb-1.5">
                      {occurrencePreview.dates.length} occurrence{occurrencePreview.dates.length !== 1 ? 's' : ''}
                    </div>
                    <div className="text-[0.85rem] text-body mb-2">
                      {formatPattern(p)}
                    </div>
                    <div className="max-h-[150px] overflow-y-auto text-[0.78rem] text-body">
                      {occurrencePreview.dates.slice(0, 10).map((d) => (
                        <div key={d.scheduledAt}>
                          • {new Date(d.scheduledAt).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })} at {d.time}
                        </div>
                      ))}
                      {occurrencePreview.dates.length > 10 && (
                        <div className="text-muted mt-1">...and {occurrencePreview.dates.length - 10} more</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}


      {/* STEP 2: Who */}
      {step === 2 && (
        <div>
          <div className="flex gap-3 mb-4">
            {['self', 'thirdParty'].map((opt) => (
              <button key={opt} onClick={() => update('bookedFor', opt)} className="flex-1 p-3.5 rounded-xl cursor-pointer font-semibold"
                style={{
                  border: `2px solid ${booking.bookedFor === opt ? '#2dd4a8' : '#e2e8f0'}`,
                  background: booking.bookedFor === opt ? 'rgba(45,212,168,0.04)' : '#fff',
                }}>{opt === 'self' ? 'For myself' : 'For someone else'}</button>
            ))}
          </div>
          {booking.bookedFor === 'thirdParty' && (
            <>
              <div className="form-group">
                <label className="form-label">Passenger name *</label>
                <input className="form-input" value={booking.thirdPartyName} onChange={(e) => update('thirdPartyName', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Passenger phone *</label>
                <input className="form-input" type="tel" value={booking.thirdPartyPhone} onChange={(e) => update('thirdPartyPhone', e.target.value)} />
              </div>
            </>
          )}
          <div className="form-group mt-4">
            <label className="flex items-center gap-2.5 cursor-pointer">
              <input type="checkbox" checked={booking.hasMinor} onChange={(e) => update('hasMinor', e.target.checked)} />
              <span className="form-label m-0">Minor passenger (under 18) — requires WWCC-verified driver</span>
            </label>
          </div>
          <div className="form-group mt-2">
            <label className="flex items-center gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={booking.bookingRiderTravelling !== false}
                onChange={(e) => update('bookingRiderTravelling', e.target.checked)}
              />
              <span className="form-label m-0">
                I will be travelling on this trip
              </span>
            </label>
            {booking.hasMinor && !booking.bookingRiderTravelling && (
              <div className="text-[0.8rem] mt-1" style={{ color: '#ff6b6b' }}>
                Unaccompanied minors are not supported. An adult on the booking must travel.
              </div>
            )}
          </div>

          {/* Multi-vehicle toggle (Batch 13) */}
          <div className="p-3.5 rounded-xl border-[1.5px] border-slate-200 mt-4"
            style={{ background: booking.isMultiVehicle ? 'rgba(139,92,246,0.04)' : '#fff' }}>
            <label className="flex items-center gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={!!booking.isMultiVehicle}
                onChange={(e) => {
                  update('isMultiVehicle', e.target.checked);
                  if (e.target.checked && (booking.vehicles || []).length === 0) {
                    // Initialise with 2 sedans
                    update('vehicles', [
                      { tier: 'go', passengerCount: 1, passengerGroupName: 'Group 1', luggage: { small: 0, large: 0 }, childSeatRequired: false, accessibilityNeeded: false, hasMinor: false },
                      { tier: 'go', passengerCount: 1, passengerGroupName: 'Group 2', luggage: { small: 0, large: 0 }, childSeatRequired: false, accessibilityNeeded: false, hasMinor: false },
                    ]);
                  }
                }}
                disabled={booking.isRecurring}
              />
              <span className="font-semibold text-brand-dark">This booking needs multiple vehicles</span>
            </label>
            <div className="text-[0.78rem] text-muted mt-1 ml-[26px]">
              {booking.isRecurring
                ? 'Multi-vehicle is not available with recurring bookings in v1.'
                : `Dispatch up to ${MAX_VEHICLES_PER_GROUP} vehicles for a single trip — useful for larger groups travelling together.`}
            </div>
          </div>

          {/* Multi-vehicle configuration */}
          {booking.isMultiVehicle && (() => {
            const vehicles = booking.vehicles || [];
            const addVehicle = () => {
              if (vehicles.length >= MAX_VEHICLES_PER_GROUP) return;
              update('vehicles', [...vehicles, {
                tier: 'go', passengerCount: 1, passengerGroupName: `Group ${vehicles.length + 1}`,
                luggage: { small: 0, large: 0 }, childSeatRequired: false, accessibilityNeeded: false, hasMinor: false,
              }]);
            };
            const removeVehicle = (idx) => {
              if (vehicles.length <= 2) {
                toast.error('Minimum 2 vehicles for a multi-vehicle booking');
                return;
              }
              update('vehicles', vehicles.filter((_, i) => i !== idx));
            };
            const updateVehicle = (idx, field, value) => {
              update('vehicles', vehicles.map((v, i) => i === idx ? { ...v, [field]: value } : v));
            };
            const totalPax = vehicles.reduce((s, v) => s + (v.passengerCount || 0), 0);
            const validation = validateMultiVehicleConfig(vehicles, totalPax);

            return (
              <div className="mt-4">
                <div className="text-[0.85rem] text-body font-semibold mb-2">
                  Total: {vehicles.length} vehicle{vehicles.length !== 1 ? 's' : ''}, {totalPax} passenger{totalPax !== 1 ? 's' : ''}
                </div>

                {vehicles.map((v, idx) => {
                  const tierInfo = TIER_CAPACITIES[v.tier];
                  const overCapacity = tierInfo && v.passengerCount > tierInfo.maxPax;
                  return (
                    <div key={idx} className="p-3.5 rounded-xl bg-white mb-3"
                      style={{ border: `1.5px solid ${overCapacity ? '#ff6b6b' : '#e2e8f0'}` }}>
                      <div className="flex justify-between items-center mb-2.5">
                        <span className="font-bold text-brand-dark">Vehicle {idx + 1}</span>
                        {vehicles.length > 2 && (
                          <button onClick={() => removeVehicle(idx)} className="px-2.5 py-1 rounded-md bg-white cursor-pointer text-[0.78rem] font-semibold"
                            style={{ border: '1px solid #ff6b6b', color: '#ff6b6b' }}>Remove</button>
                        )}
                      </div>

                      <div className="form-group mb-2.5">
                        <label className="form-label">Group label (optional)</label>
                        <input
                          className="form-input"
                          value={v.passengerGroupName || ''}
                          onChange={(e) => updateVehicle(idx, 'passengerGroupName', e.target.value)}
                          placeholder={`Group ${idx + 1}`}
                        />
                      </div>

                      <div className="form-group mb-2.5">
                        <label className="form-label">Service tier *</label>
                        <select
                          className="form-input form-select"
                          value={v.tier}
                          onChange={(e) => updateVehicle(idx, 'tier', e.target.value)}
                        >
                          {Object.entries(TIER_CAPACITIES).map(([tierKey, info]) => (
                            <option key={tierKey} value={tierKey}>{info.label} (up to {info.maxPax})</option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group mb-2.5">
                        <label className="form-label">Passengers in this vehicle *</label>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => updateVehicle(idx, 'passengerCount', Math.max(1, v.passengerCount - 1))}
                            className="w-9 h-9 rounded-full border-[1.5px] border-slate-200 bg-white cursor-pointer"
                          >−</button>
                          <span className="font-bold min-w-[24px] text-center" style={{ color: overCapacity ? '#ff6b6b' : '#0a1628' }}>
                            {v.passengerCount}
                          </span>
                          <button
                            onClick={() => updateVehicle(idx, 'passengerCount', Math.min(tierInfo?.maxPax || 4, v.passengerCount + 1))}
                            className="w-9 h-9 rounded-full border-[1.5px] border-slate-200 bg-white cursor-pointer"
                          >+</button>
                          <span className="text-[0.78rem] text-muted">
                            (max {tierInfo?.maxPax || 4})
                          </span>
                        </div>
                        {overCapacity && (
                          <div className="text-[0.75rem] mt-1" style={{ color: '#ff6b6b' }}>
                            Exceeds capacity. Upgrade tier or reduce count.
                          </div>
                        )}
                      </div>

                      <label className="flex items-center gap-2 text-[0.85rem] cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!v.hasMinor}
                          onChange={(e) => updateVehicle(idx, 'hasMinor', e.target.checked)}
                        />
                        <span>This vehicle has minor passenger(s)</span>
                      </label>
                      <label className="flex items-center gap-2 text-[0.85rem] cursor-pointer mt-1">
                        <input
                          type="checkbox"
                          checked={!!v.childSeatRequired}
                          onChange={(e) => updateVehicle(idx, 'childSeatRequired', e.target.checked)}
                        />
                        <span>Child seat required</span>
                      </label>
                      <label className="flex items-center gap-2 text-[0.85rem] cursor-pointer mt-1">
                        <input
                          type="checkbox"
                          checked={!!v.accessibilityNeeded}
                          onChange={(e) => updateVehicle(idx, 'accessibilityNeeded', e.target.checked)}
                        />
                        <span>Accessibility needed</span>
                      </label>
                    </div>
                  );
                })}

                <button
                  onClick={addVehicle}
                  disabled={vehicles.length >= MAX_VEHICLES_PER_GROUP}
                  className="w-full p-3 rounded-xl border-[1.5px] border-dashed font-bold text-[0.9rem]"
                  style={{
                    borderColor: '#8b5cf6', background: '#faf5ff', color: '#7c3aed',
                    cursor: vehicles.length >= MAX_VEHICLES_PER_GROUP ? 'not-allowed' : 'pointer',
                    opacity: vehicles.length >= MAX_VEHICLES_PER_GROUP ? 0.5 : 1,
                  }}
                >+ Add another vehicle ({vehicles.length}/{MAX_VEHICLES_PER_GROUP})</button>

                {!validation.valid && (
                  <div className="p-2.5 rounded-lg mt-3 text-[0.85rem]" style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626' }}>
                    ⚠ {validation.error}
                  </div>
                )}

                <div className="p-3 rounded-[10px] bg-slate-50 mt-3 text-[0.78rem] text-body leading-normal">
                  <strong className="text-brand-dark">How it works:</strong> Each vehicle is matched to its own driver in parallel.
                  Each vehicle has its own Trip PIN, fare, and Passenger Service Levy. You're the booker and payer for all vehicles.
                </div>
              </div>
            );
          })()}
        </div>
      )}


      {/* STEP 3: Where */}
      {step === 3 && (
        <div>
          <div className="form-group">
            <label className="form-label">Pickup address *</label>
            <input id="pickup-input" className="form-input" placeholder="Search for pickup location..." defaultValue={booking.pickupAddress} />
          </div>
          <div className="form-group">
            <label className="form-label">Destination *</label>
            <input id="destination-input" className="form-input" placeholder="Search for destination..." defaultValue={booking.destinationAddress} />
          </div>

          {routesLoading && (
            <div className="p-3.5 rounded-xl bg-slate-50 mt-3 text-center text-muted text-[0.9rem]">
              Finding the best routes...
            </div>
          )}

          {!routesLoading && routeOptions.routes.length > 0 && (
            <div className="mt-3">
              <div className="text-[0.85rem] text-body font-semibold mb-2">
                Choose your route
              </div>
              {routeOptions.routes.slice(0, 2).map((route, idx) => {
                const isSelected = selectedRouteIndex === idx;
                const isRecommended = route.index === routeOptions.recommended?.index;
                return (
                  <div key={idx} onClick={() => setSelectedRouteIndex(idx)} className="p-3.5 rounded-xl mb-2.5 cursor-pointer"
                    style={{
                      border: `2px solid ${isSelected ? '#2dd4a8' : '#e2e8f0'}`,
                      background: isSelected ? 'rgba(45,212,168,0.04)' : '#fff',
                    }}>
                    <div className="flex justify-between items-start mb-1.5">
                      <div>
                        <div className="font-bold text-brand-dark text-[0.95rem]">
                          via {route.summary}
                          {isRecommended && (
                            <span className="ml-2 px-2 py-0.5 rounded-full text-white text-[0.7rem] font-bold" style={{ background: '#2dd4a8' }}>
                              RECOMMENDED
                            </span>
                          )}
                        </div>
                        <div className="text-[0.8rem] text-body mt-0.5">
                          {route.distanceKm}km · ~{route.durationMin} min
                        </div>
                      </div>
                      {route.hasTolls ? (
                        <div className="text-right whitespace-nowrap">
                          <div className="text-[0.7rem] text-muted uppercase font-semibold">Tolls</div>
                          <div className="font-bold text-[1rem] text-brand-dark">${route.totalToll.toFixed(2)}</div>
                        </div>
                      ) : (
                        <div className="px-2.5 py-1 rounded-full text-[0.75rem] font-semibold" style={{ background: '#f0fdf4', color: '#059669' }}>
                          No tolls
                        </div>
                      )}
                    </div>

                    {route.tolls.length > 0 && (
                      <div className="mt-1.5 pt-1.5 border-t border-slate-100">
                        {route.tolls.map(t => (
                          <div key={t.id} className="text-[0.75rem] text-muted">
                            • {t.name}: ${t.cost.toFixed(2)} {t.estimate ? '(estimated)' : t.isPeak ? '(peak)' : t.isWeekend ? '(weekend)' : '(off-peak)'}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              {routeOptions.recommended && routeOptions.alternative && (() => {
                const tollSavings = routeOptions.alternative.totalToll - routeOptions.recommended.totalToll;
                const timeDiff = routeOptions.alternative.durationMin - routeOptions.recommended.durationMin;
                if (tollSavings > 0.5 && timeDiff > -3) {
                  return (
                    <div className="text-[0.8rem] text-body text-center mt-1">
                      Save ${tollSavings.toFixed(2)} in tolls with the recommended route
                    </div>
                  );
                }
                if (timeDiff < -3) {
                  return (
                    <div className="text-[0.8rem] text-body text-center mt-1">
                      The alternative is {Math.abs(timeDiff)} min faster but costs ${(-tollSavings).toFixed(2)} more in tolls
                    </div>
                  );
                }
                return null;
              })()}

            </div>
          )}

          {!routesLoading && booking.pickup && booking.destination && routeOptions.routes.length === 0 && (
            <div className="p-3.5 rounded-xl mt-3 text-[0.85rem]" style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626' }}>
              No routes found. Try different addresses.
            </div>
          )}
        </div>
      )}


      {/* STEP 4: Stops (Batch 11 — multi-stop bookings) */}
      {step === 4 && (() => {
        const addStop = () => {
          if ((booking.stops || []).length >= 4) {
            toast.error('Maximum 4 intermediate stops');
            return;
          }
          const newStop = {
            type: 'dropoff',
            address: '',
            location: null,
            coRiderName: '',
            passengerCountChange: -1,
            expectedDwellSeconds: 60,
          };
          update('stops', [...(booking.stops || []), newStop]);
        };
        const removeStop = (idx) => {
          const next = (booking.stops || []).filter((_, i) => i !== idx);
          update('stops', next);
        };
        const updateStopField = (idx, field, value) => {
          const next = (booking.stops || []).map((s, i) => i === idx ? { ...s, [field]: value } : s);
          update('stops', next);
        };
        const moveStop = (idx, dir) => {
          const stops = [...(booking.stops || [])];
          const newIdx = idx + dir;
          if (newIdx < 0 || newIdx >= stops.length) return;
          [stops[idx], stops[newIdx]] = [stops[newIdx], stops[idx]];
          update('stops', stops);
        };
        const initStopAutocomplete = (idx) => {
          if (!window.google) return;
          const input = document.getElementById(`stop-input-${idx}`);
          if (!input) return;
          const ac = new window.google.maps.places.Autocomplete(input, {
            componentRestrictions: { country: 'au' }, fields: ['geometry', 'formatted_address', 'place_id'],
          });
          ac.addListener('place_changed', () => {
            const place = ac.getPlace();
            if (place.geometry) {
              const next = (booking.stops || []).map((s, i) => i === idx ? {
                ...s,
                address: place.formatted_address,
                location: {
                  lat: place.geometry.location.lat(),
                  lng: place.geometry.location.lng(),
                  placeId: place.place_id,
                },
              } : s);
              update('stops', next);
            }
          });
        };

        return (
          <div>
            <p className="text-body text-[0.9rem] mb-4">
              Add up to 4 intermediate stops along your trip. Each stop is either a passenger pickup or drop-off.
              Stops are quick — a 1-minute free buffer applies, then $0.50/min beyond that.
            </p>

            {booking.estimatedDistance > 0 && (
              <div className="flex items-center justify-between gap-3 p-3 rounded-xl mb-4" style={{ background: '#f0fdfa', border: '1px solid #99f6e4' }}>
                <div>
                  <div className="text-[0.85rem] font-semibold text-brand-dark">Estimated total</div>
                  <div className="text-[0.72rem] text-muted">Updates as you add stops · final total shown at review</div>
                </div>
                <span className="font-extrabold text-[1.05rem] text-brand-dark">
                  ${(booking.isMultiVehicle ? getMultiVehicleFares().grandTotal : (getSelectedFare()?.totalFare || 0)).toFixed(2)}
                </span>
              </div>
            )}

            {(booking.stops || []).length === 0 && (
              <div className="p-6 rounded-xl bg-slate-50 text-center text-muted mb-4" style={{ border: '1px dashed #cbd5e1' }}>
                No stops yet. Tap + Add a stop below if you need to pick someone up or drop someone off along the way.
              </div>
            )}

            {(booking.stops || []).map((s, idx) => (
              <div key={idx} className="p-4 rounded-xl border-[1.5px] border-slate-200 bg-white mb-3">
                <div className="flex justify-between items-center mb-2.5">
                  <span className="font-bold text-brand-dark">Stop {idx + 1}</span>
                  <div className="flex gap-1.5">
                    <button onClick={() => moveStop(idx, -1)} disabled={idx === 0} className="px-2 py-1 rounded-md border border-slate-200 bg-white text-[0.85rem]"
                      style={{ cursor: idx === 0 ? 'not-allowed' : 'pointer', opacity: idx === 0 ? 0.4 : 1 }}>↑</button>
                    <button onClick={() => moveStop(idx, 1)} disabled={idx === booking.stops.length - 1} className="px-2 py-1 rounded-md border border-slate-200 bg-white text-[0.85rem]"
                      style={{
                        cursor: idx === booking.stops.length - 1 ? 'not-allowed' : 'pointer',
                        opacity: idx === booking.stops.length - 1 ? 0.4 : 1,
                      }}>↓</button>
                    <button onClick={() => removeStop(idx)} className="px-2.5 py-1 rounded-md bg-white cursor-pointer text-[0.85rem] font-semibold"
                      style={{ border: '1px solid #ff6b6b', color: '#ff6b6b' }}>Remove</button>
                  </div>
                </div>

                <div className="form-group mb-2.5">
                  <label className="form-label">Type</label>
                  <div className="flex gap-2">
                    <button onClick={() => updateStopField(idx, 'type', 'pickup')} className="flex-1 p-2.5 rounded-lg cursor-pointer font-semibold text-[0.85rem]"
                      style={{
                        border: `2px solid ${s.type === 'pickup' ? '#2dd4a8' : '#e2e8f0'}`,
                        background: s.type === 'pickup' ? 'rgba(45,212,168,0.06)' : '#fff',
                      }}>🧍 Pickup</button>
                    <button onClick={() => updateStopField(idx, 'type', 'dropoff')} className="flex-1 p-2.5 rounded-lg cursor-pointer font-semibold text-[0.85rem]"
                      style={{
                        border: `2px solid ${s.type === 'dropoff' ? '#2dd4a8' : '#e2e8f0'}`,
                        background: s.type === 'dropoff' ? 'rgba(45,212,168,0.06)' : '#fff',
                      }}>👋 Drop-off</button>
                  </div>
                </div>

                <div className="form-group mb-2.5">
                  <label className="form-label">Address</label>
                  <input
                    id={`stop-input-${idx}`}
                    className="form-input"
                    placeholder="Search for address..."
                    defaultValue={s.address || ''}
                    onFocus={() => initStopAutocomplete(idx)}
                  />
                </div>

                {s.type === 'pickup' && (
                  <div className="form-group mb-2.5">
                    <label className="form-label">Passenger first name</label>
                    <input
                      className="form-input"
                      placeholder="e.g. Sarah"
                      value={s.coRiderName || ''}
                      onChange={(e) => updateStopField(idx, 'coRiderName', e.target.value)}
                    />
                    <div className="text-[0.75rem] text-muted mt-1">
                      You're responsible for ensuring this person is at the pickup point on time. We won't contact them.
                    </div>
                  </div>
                )}
              </div>
            ))}

            <button onClick={addStop} disabled={(booking.stops || []).length >= 4} className="w-full p-3.5 rounded-xl border-[1.5px] border-dashed font-bold text-[0.9rem]"
              style={{
                borderColor: '#2dd4a8', background: '#f0fdf4', color: '#059669',
                cursor: (booking.stops || []).length >= 4 ? 'not-allowed' : 'pointer',
                opacity: (booking.stops || []).length >= 4 ? 0.5 : 1,
              }}>
              + Add a stop ({(booking.stops || []).length}/4)
            </button>
          </div>
        );
      })()}

      {/* STEP 8: Driver */}
      {step === 8 && (
        <div>
          <div className="flex flex-col gap-3">
            <button onClick={() => update('driverPreference', 'auto')} className="p-4 rounded-xl text-left cursor-pointer"
              style={{
                border: `2px solid ${booking.driverPreference === 'auto' ? '#2dd4a8' : '#e2e8f0'}`,
                background: booking.driverPreference === 'auto' ? 'rgba(45,212,168,0.04)' : '#fff',
              }}>
              <div className="font-bold text-brand-dark">Auto-assign best available</div>
              <div className="text-[0.85rem] text-body">We'll match you with the best driver for your trip</div>
            </button>
            <button onClick={() => update('driverPreference', 'preferred')} className="p-4 rounded-xl text-left cursor-pointer"
              style={{
                border: `2px solid ${booking.driverPreference === 'preferred' ? '#2dd4a8' : '#e2e8f0'}`,
                background: booking.driverPreference === 'preferred' ? 'rgba(45,212,168,0.04)' : '#fff',
              }}>
              <div className="font-bold text-brand-dark">{booking.isMultiVehicle ? 'Choose preferred drivers' : 'Choose a preferred driver'}</div>
              <div className="text-[0.85rem] text-body">{booking.isMultiVehicle ? "Pick one driver per vehicle from drivers you've ridden with" : "Select from drivers you've ridden with before"}</div>
            </button>
          </div>
          {booking.driverPreference === 'preferred' && (() => {
            const pickupDate = booking.date && booking.time ? new Date(`${booking.date}T${booking.time}`) : null;
            const minutesUntilPickup = pickupDate ? (pickupDate - Date.now()) / 60000 : Infinity;
            const isShortNotice = minutesUntilPickup < 90;
            const TIER_BADGE = { gold: { bg: '#fef3c7', color: '#92400e', label: 'Gold' }, silver: { bg: '#f1f5f9', color: '#475569', label: 'Silver' }, bronze: { bg: '#fff1e6', color: '#9a3412', label: 'Bronze' } };

            // Multi-vehicle: rider picks a pool of preferred drivers (one per vehicle)
            const isMulti = booking.isMultiVehicle;
            const poolCap = (booking.vehicles || []).length || 1;
            const pool = booking.preferredDriverPool || [];
            const togglePoolDriver = (id) => {
              const cur = booking.preferredDriverPool || [];
              if (cur.includes(id)) {
                update('preferredDriverPool', cur.filter((x) => x !== id));
              } else if (cur.length >= poolCap) {
                toast.error(`You can select up to ${poolCap} drivers (one per vehicle)`, { id: 'pool-cap' });
              } else {
                update('preferredDriverPool', [...cur, id]);
              }
            };

            const renderDriverRow = (d) => {
              const isSelected = isMulti ? pool.includes(d.id) : booking.preferredDriverId === d.id;
              const badge = TIER_BADGE[d.qualityTier] || null;
              // Display name from cloud function as `displayName` (masked); fall back to legacy `name`
              const displayName = d.displayName || d.name || 'Driver';
              const vehicleLine = [d.vehicleColour, d.vehicleMake, d.vehicleModel].filter(Boolean).join(' ');
              // Compatibility check: dim row if vehicle can't fit chosen passenger count
              const incompatible = d.vehicleMaxPassengers && booking.passengerCount > d.vehicleMaxPassengers;
              return (
                <div key={d.id} onClick={() => { if (incompatible) return; isMulti ? togglePoolDriver(d.id) : update('preferredDriverId', d.id); }} className="p-3.5 rounded-xl mb-2 flex items-center gap-3"
                  style={{
                    cursor: incompatible ? 'not-allowed' : 'pointer',
                    opacity: incompatible ? 0.5 : 1,
                    border: `2px solid ${isSelected ? '#2dd4a8' : '#e2e8f0'}`,
                    background: isSelected ? 'rgba(45,212,168,0.04)' : '#fff',
                  }}>
                  <div className="w-11 h-11 rounded-full bg-slate-200 flex items-center justify-center font-bold">
                    {displayName[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-brand-dark">{displayName}</div>
                    {vehicleLine && (
                      <div className="text-[0.8rem] text-body">
                        {vehicleLine}
                        {d.vehicleMaxPassengers && <span className="text-muted"> · up to {d.vehicleMaxPassengers} pax</span>}
                      </div>
                    )}
                    <div className="text-[0.78rem] text-body mt-0.5">
                      ★ {(d.rating || 0).toFixed(1)}
                      {d.plate && <span className="ml-2 px-1.5 py-px rounded font-bold" style={{ background: '#fef9c3', border: '1px solid #fde68a', color: '#713f12', letterSpacing: '0.04em' }}>{d.plate}</span>}
                    </div>
                    {incompatible && (
                      <div className="text-[0.75rem] mt-0.5" style={{ color: '#ff6b6b' }}>
                        Doesn't fit {booking.passengerCount} passengers
                      </div>
                    )}
                  </div>
                  {badge && (
                    <span className="px-2.5 py-[3px] rounded-full text-[0.7rem] font-bold" style={{ background: badge.bg, color: badge.color }}>
                      {badge.label}
                    </span>
                  )}
                </div>
              );
            };


            const handleShowAll = async () => {
              if (allQualifiedDrivers.length > 0) {
                setShowAllQualified(true);
                return;
              }
              setLoadingQualified(true);
              try {
                const result = await getQualifiedDriversList({
                  tier: booking.tier || 'go',
                  passengerCount: booking.passengerCount || 1,
                  hasMinor: !!booking.hasMinor,
                  childSeatRequired: !!booking.childSeatRequired,
                });
                // Filter out drivers already shown in past list to avoid duplicates
                const pastIds = new Set(preferredDrivers.map(d => d.id));
                setAllQualifiedDrivers((result.drivers || []).filter(d => !pastIds.has(d.id)));
                setShowAllQualified(true);
              } catch (err) {
                toast.error('Could not load drivers — try again');
              }
              setLoadingQualified(false);
            };

            return (
              <div className="mt-4">
                {isShortNotice && (
                  <div className="p-3 rounded-xl mb-3 text-[0.85rem]" style={{ background: '#fff7ed', border: '1px solid #fed7aa', color: '#9a3412' }}>
                    ⚠ <strong>Short notice — driver may decline.</strong> They have only {Math.max(10, Math.round(minutesUntilPickup - 30))} minutes to respond. If they don't, your booking is opened to all qualified drivers automatically.
                  </div>
                )}

                {isMulti && (
                  <div className="p-3 rounded-xl mb-3 text-[0.85rem]" style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', color: '#5b21b6' }}>
                    Pick up to {poolCap} preferred drivers (one per vehicle). We'll offer each vehicle to one of your chosen drivers first; any vehicle we can't fill is auto-assigned. Selected {pool.length} of {poolCap}.
                  </div>
                )}

                {/* Past drivers section */}
                {preferredDrivers.length > 0 && (
                  <>
                    <div className="text-[0.85rem] text-body font-semibold mb-2">
                      Drivers you've ridden with
                    </div>
                    {preferredDrivers.map(renderDriverRow)}
                  </>
                )}

                {preferredDrivers.length === 0 && !showAllQualified && (
                  <div className="p-[18px] text-center text-muted text-[0.9rem] mb-3">
                    No previous drivers yet.
                  </div>
                )}

                {/* Toggle to show all qualified drivers */}
                {!showAllQualified ? (
                  <button onClick={handleShowAll} disabled={loadingQualified} className="w-full p-3.5 rounded-xl border-[1.5px] border-dashed font-bold cursor-pointer text-[0.9rem]"
                    style={{ borderColor: '#2dd4a8', background: '#f0fdf4', color: '#059669' }}>
                    {loadingQualified ? 'Loading...' : '+ See all qualified drivers'}
                  </button>
                ) : (
                  <>
                    {preferredDrivers.length > 0 && (
                      <div className="text-[0.85rem] text-body font-semibold mt-4 mb-2">
                        All qualified drivers
                      </div>
                    )}
                    {allQualifiedDrivers.length === 0 ? (
                      <div className="p-[18px] text-center text-muted text-[0.9rem]">
                        No qualified drivers available right now.
                      </div>
                    ) : (
                      allQualifiedDrivers.map(renderDriverRow)
                    )}
                  </>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* STEP 5: Passengers & Luggage */}

      {/* Passengers & luggage step removed — the rider picks a car class (capacity shown on each card) */}


      {/* STEP 6: Accessibility (+ Compliance/vulnerable-passenger fields — Batch 10) */}
      {step === 6 && (
        <div>
          {renderSpecialInstructions()}
          {renderEmergencyContact()}
        </div>
      )}


      {/* STEP 7: Service Tier */}
      {step === 7 && (
        <div className="flex flex-col gap-3">
          {/* Accessibility shown ON TOP so riders don't miss it before picking a car */}
          <div className="p-4 rounded-2xl border border-slate-200 bg-white">
            <div className="font-semibold text-brand-dark text-[0.95rem] mb-2">Any accessibility needs? (optional)</div>
            <label className="flex items-center gap-2.5 mb-2 cursor-pointer">
              <input type="checkbox" checked={booking.childSeatRequired} onChange={(e) => update('childSeatRequired', e.target.checked)} />
              <span>Child seat required</span>
            </label>
            <label className="flex items-center gap-2.5 mb-2 cursor-pointer">
              <input type="checkbox" checked={booking.accessibilityNeeded} onChange={(e) => update('accessibilityNeeded', e.target.checked)} />
              <span>Wheelchair accessible vehicle needed</span>
            </label>
            <label className="flex items-center gap-2.5 cursor-pointer">
              <input type="checkbox" checked={booking.assistanceRequired} onChange={(e) => update('assistanceRequired', e.target.checked)} />
              <span>Assistance required at pickup/dropoff</span>
            </label>
            <details className="mt-3">
              <summary className="cursor-pointer text-[0.9rem] text-brand-dark font-medium">More options &amp; special requests</summary>
              <div className="mt-3">
                {renderAdditionalSupport()}
                {renderSpecialInstructions()}
                {renderEmergencyContact()}
              </div>
            </details>
          </div>
          {fareEstimates.length > 0 ? (
            fareEstimates.map((fare) => {
              const maxPax = { go: 4, comfy: 4, plus: 6, vip: 4, vipPlus: 6 }[fare.tier] || 4;
              const maxLarge = { go: 2, comfy: 2, plus: 4, vip: 2, vipPlus: 4 }[fare.tier] || 2;
              const maxSmall = { go: 2, comfy: 3, plus: 6, vip: 3, vipPlus: 6 }[fare.tier] || 2;
              return (
                <div key={fare.tier} onClick={() => { update('tier', fare.tier); }} className="p-5 rounded-2xl cursor-pointer transition-all"
                  style={{
                    border: booking.tier === fare.tier ? '2px solid #2dd4a8' : '2px solid #e2e8f0',
                    background: booking.tier === fare.tier ? 'rgba(45,212,168,0.04)' : '#fff',
                  }}>
                  <div className="flex justify-between items-center mb-2">
                    <div>
                      <div className="font-bold text-[1.05rem] text-brand-dark">
                        {{ go: 'Reservo Go', comfy: 'Reservo Comfy', plus: 'Reservo Plus', vip: 'Reservo VIP', vipPlus: 'Reservo VIP+' }[fare.tier]}
                      </div>
                      <div className="text-[0.85rem] text-muted">
                        {{ go: 'Sedan', comfy: 'Premium', plus: 'SUV / Van', vip: 'Luxury Sedan', vipPlus: 'Luxury SUV' }[fare.tier]} · Up to {maxPax} seats · {maxSmall} small + {maxLarge} large bags
                      </div>
                    </div>
                    <div className="font-extrabold text-[1.3rem] text-brand-dark">${fare.totalFare.toFixed(2)}</div>
                  </div>
                  <div className="text-[0.8rem] text-muted">
                    {fare.distanceKm}km · ~{fare.durationMin}min
                    {fare.peakPercent > 0 && <span style={{ color: '#ff6b6b' }}> · {fare.peakPercent}% peak</span>}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="p-8 text-center text-muted">
              Complete pickup and destination to see fare estimates
            </div>
          )}
          <div className="text-[0.8rem] text-muted text-center mt-1">
            Pick the car that fits your group and bags — you choose the space you need.
          </div>
        </div>
      )}


      {/* STEP 9: Review & Pay */}
      {step === 9 && (() => {
        // Compatibility check at review time (rider may have gone back and changed values)
        let compatIssue = null;
        if (booking.driverPreference === 'preferred' && booking.preferredDriverId) {
          const allDrivers = [...preferredDrivers, ...allQualifiedDrivers];
          const sel = allDrivers.find((d) => d.id === booking.preferredDriverId);
          if (sel && sel.vehicleMaxPassengers && booking.passengerCount > sel.vehicleMaxPassengers) {
            compatIssue = {
              driverName: sel.displayName || sel.name || 'Driver',
              vehicleMax: sel.vehicleMaxPassengers,
              chosen: booking.passengerCount,
            };
          }
        }
        return (
        <div>
          {compatIssue && (
            <div className="p-3.5 rounded-xl mb-4" style={{ background: '#fef2f2', border: '1px solid #fecaca' }}>
              <div className="font-bold mb-1" style={{ color: '#dc2626' }}>
                ⚠ Selected driver is no longer compatible
              </div>
              <div className="text-[0.85rem] text-body mb-2">
                You picked <strong>{compatIssue.driverName}</strong>, whose vehicle holds up to {compatIssue.vehicleMax} passengers — but you've selected {compatIssue.chosen}.
              </div>
              <button onClick={() => { update('preferredDriverId', null); update('driverPreference', 'auto'); }} className="px-3 py-2 rounded-lg bg-white cursor-pointer font-semibold text-[0.85rem]"
                style={{ border: '1px solid #dc2626', color: '#dc2626' }}>Switch to auto-assign</button>
            </div>
          )}
          <div className="p-5 rounded-2xl border border-slate-200 mb-4">
            <h3 className="mt-0 mb-4 font-serif">Booking Summary</h3>
            <div className="grid gap-2 text-[0.9rem]">
              {booking.isRecurring ? (
                <div>
                  <strong>Schedule:</strong> {formatPattern(booking.recurrencePattern)} — {occurrencePreview.dates.length} occurrences
                </div>
              ) : (
                <div><strong>Date/Time:</strong> {booking.date} at {booking.time}</div>
              )}
              {booking.isMultiVehicle && (
                <div>
                  <strong>Vehicles:</strong> {(booking.vehicles || []).length} ({(booking.vehicles || []).reduce((s, v) => s + v.passengerCount, 0)} passengers total)
                  <ul className="mt-1 mb-0 pl-[18px]">
                    {(booking.vehicles || []).map((v, i) => (
                      <li key={i} className="text-[0.82rem] text-body">
                        {v.passengerGroupName || `Vehicle ${i + 1}`}: {TIER_CAPACITIES[v.tier]?.label} · {v.passengerCount} pax
                      </li>
                    ))}
                  </ul>
                  {booking.driverPreference === 'preferred' && (booking.preferredDriverPool || []).length > 0 && (
                    <div className="text-[0.82rem] text-body mt-1">
                      Preferred drivers: {(booking.preferredDriverPool || []).map((id) => {
                        const all = [...preferredDrivers, ...allQualifiedDrivers];
                        const d = all.find((x) => x.id === id);
                        return d?.displayName || d?.name || 'Driver';
                      }).join(', ')} <span className="text-muted">(assigned across vehicles; unfilled auto-assign)</span>
                    </div>
                  )}
                </div>
              )}
              <div><strong>Pickup:</strong> {booking.pickupAddress}</div>
              {(booking.stops || []).length > 0 && (
                <div>
                  <strong>Stops:</strong>
                  <ol className="mt-1 mb-0 pl-[18px]">
                    {booking.stops.map((s, i) => (
                      <li key={i} className="text-[0.85rem] text-body">
                        {s.type === 'pickup' ? `🧍 Pickup — ${s.coRiderName}` : '👋 Drop-off'} — {s.address}
                      </li>
                    ))}
                  </ol>
                </div>
              )}
              <div><strong>Destination:</strong> {booking.destinationAddress}</div>
              {!booking.isMultiVehicle && (
                <>
                  <div><strong>Tier:</strong> {booking.tier.charAt(0).toUpperCase() + booking.tier.slice(1)}</div>
                  <div><strong>Car:</strong> Up to {({ go: 4, comfy: 4, plus: 6, vip: 4, vipPlus: 6 }[booking.tier] || 4)} seats {booking.bookingRiderTravelling === false && '(you are not travelling)'}</div>
                </>
              )}
              {booking.bookedFor === 'thirdParty' && <div><strong>Booked for:</strong> {booking.thirdPartyName}</div>}
            </div>
          </div>

          {/* Multi-vehicle fare breakdown — per vehicle, itemised + grand total */}
          {booking.isMultiVehicle && getMultiVehicleFares().vehicles.length > 0 && (
            <div className="p-5 rounded-2xl bg-slate-50 mb-4">
              <h4 className="mt-0 mb-3">Fare Breakdown</h4>
              {getMultiVehicleFares().vehicles.map((v, i) => {
                const fb = v.fareBreakdown || {};
                const items = [
                  ['Base fare', fb.baseFare],
                  ['Distance', fb.distanceFare],
                  ['Duration', fb.durationFare],
                  fb.peakSurcharge > 0 && [`Peak surcharge${fb.peakPercent ? ` (${fb.peakPercent}%)` : ''}`, fb.peakSurcharge],
                  fb.tolls > 0 && ['Tolls', fb.tolls],
                  fb.ctp > 0 && ['NSW CTP levy', fb.ctp],
                  fb.bookingFee > 0 && ['Booking fee', fb.bookingFee],
                  fb.dwellCharges > 0 && [`Stop dwell (${fb.stopsCount} stop${fb.stopsCount !== 1 ? 's' : ''})`, fb.dwellCharges],
                  ['Passenger levy', fb.levy],
                  ['GST (10%)', fb.gst],
                ].filter(Boolean);
                return (
                  <div key={i} className="mb-3 pb-3 border-b border-slate-200 last:border-0 last:mb-0 last:pb-0">
                    <div className="flex justify-between items-center mb-1.5">
                      <span className="font-semibold text-brand-dark text-[0.95rem]">
                        {v.passengerGroupName || `Vehicle ${i + 1}`} · {TIER_CAPACITIES[v.tier]?.label?.split(' (')[0] || v.tier}
                      </span>
                      <span className="font-bold text-brand-dark">${(fb.totalFare || 0).toFixed(2)}</span>
                    </div>
                    {items.map(([label, amount], j) => (
                      <div key={j} className="flex justify-between py-0.5 text-[0.82rem]">
                        <span className="text-muted">{label}</span>
                        <span className="text-body">${Math.abs(Number(amount) || 0).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                );
              })}
              <div className="flex justify-between pt-3 mt-1 border-t-2 border-slate-200">
                <span className="font-extrabold text-[1.1rem]">Total ({getMultiVehicleFares().vehicles.length} vehicles)</span>
                <span className="font-extrabold text-[1.1rem] text-brand-dark">${getMultiVehicleFares().grandTotal.toFixed(2)}</span>
              </div>
              <div className="text-[0.75rem] text-muted mt-2">
                Each vehicle is priced separately by its tier and carries its own $1.20 Passenger Service Levy. You're the booker and payer for all vehicles.
              </div>
            </div>
          )}

          {/* Single-vehicle fare breakdown */}
          {!booking.isMultiVehicle && getSelectedFare() && (
            <div className="p-5 rounded-2xl bg-slate-50 mb-4">
              <h4 className="mt-0 mb-3">Fare Breakdown</h4>
              {[

                ['Base fare', getSelectedFare().baseFare],
                ['Distance', getSelectedFare().distanceFare],
                ['Duration', getSelectedFare().durationFare],
                getSelectedFare().peakSurcharge > 0 && ['Peak surcharge', getSelectedFare().peakSurcharge],
                getSelectedFare().tolls > 0 && ['Tolls', getSelectedFare().tolls],
                getSelectedFare().ctp > 0 && ['NSW CTP levy', getSelectedFare().ctp],
                getSelectedFare().bookingFee > 0 && ['Booking fee', getSelectedFare().bookingFee],
                getSelectedFare().dwellCharges > 0 && [`Stop dwell (${getSelectedFare().stopsCount} stop${getSelectedFare().stopsCount !== 1 ? 's' : ''})`, getSelectedFare().dwellCharges],
                ['Passenger levy', getSelectedFare().levy],
                ['GST (10%)', getSelectedFare().gst],
                booking.promoDiscount > 0 && ['Promo discount', -booking.promoDiscount],
              ].filter(Boolean).map(([label, amount], i) => (
                <div key={i} className="flex justify-between py-1 text-[0.9rem]">
                  <span className="text-body">{label}</span>
                  <span className="font-medium" style={{ color: amount < 0 ? '#2dd4a8' : '#0a1628' }}>
                    {amount < 0 ? '-' : ''}${Math.abs(amount).toFixed(2)}
                  </span>
                </div>
              ))}
              <div className="flex justify-between pt-3 mt-2 border-t-2 border-slate-200">
                <span className="font-extrabold text-[1.1rem]">
                  {booking.isRecurring ? 'Per trip' : 'Total'}
                </span>
                <span className="font-extrabold text-[1.1rem] text-brand-dark">
                  ${(getSelectedFare().totalFare - booking.promoDiscount).toFixed(2)}
                </span>
              </div>
              {booking.isRecurring && occurrencePreview.dates.length > 0 && (
                <div className="pt-2 mt-2 border-t border-slate-200">
                  <div className="flex justify-between text-[0.85rem] text-body">
                    <span>× {occurrencePreview.dates.length} occurrences (estimated)</span>
                    <span className="font-bold text-brand-dark">
                      ~${((getSelectedFare().totalFare - booking.promoDiscount) * occurrencePreview.dates.length).toFixed(2)}
                    </span>
                  </div>
                  <div className="text-[0.75rem] text-muted mt-1">
                    Each occurrence is priced individually based on its date and time. Payment is taken 2 hours before each trip.
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Promo Code */}
          <div className="flex gap-2 mb-4">
            <input className="form-input flex-1" placeholder="Promo code" value={booking.promoCode}
              onChange={(e) => update('promoCode', e.target.value.toUpperCase())} />
            <button onClick={handleApplyPromo} className="btn btn-secondary whitespace-nowrap">Apply</button>
          </div>

          {/* Wallet Credit */}
          <label className="flex items-center gap-2.5 mb-6 cursor-pointer">
            <input type="checkbox" checked={booking.useWalletCredit} onChange={(e) => update('useWalletCredit', e.target.checked)} />
            <span>Use wallet credit (${booking.walletCreditAmount?.toFixed(2) || '0.00'} available)</span>
          </label>
        </div>
        );
      })()}

      {/* Navigation */}
      <div className="flex gap-3 mt-8">
        {step > 1 ? (
          <button onClick={prevStep} className="btn btn-secondary flex-1">Back</button>
        ) : (
          <button onClick={() => navigate('/rider')} className="btn btn-secondary flex-1">Dashboard</button>
        )}
        {step < 9 ? (
          <button onClick={nextStep} className="btn btn-primary flex-1">Continue</button>
        ) : (
          <button onClick={handleSubmit} disabled={submitting} className="btn btn-primary flex-1">
            {submitting ? 'Booking...' : 'Confirm Booking'}
          </button>
        )}
      </div>
    </div>
  );
}